import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { MatomoContext } from "./matomo-context";
import { MatomoService } from "./matomo-service";
import type { MatomoConfig, MatomoContextValue } from "./types";
import LOCAL_STORAGE_KEYS from "@/constants/local-storage-keys";

interface MatomoProviderProps {
	children: ReactNode;
	config?: MatomoConfig;
}

/**
 * Get Matomo configuration from environment variables
 */
function getConfigFromEnv(): MatomoConfig {
	const url = "https://battmobility.matomo.cloud";
	const siteId = import.meta.env.VITE_MATOMO_SITE_ID;

	if (!siteId) {
		throw new Error(
			"Matomo configuration missing. Please set VITE_MATOMO_SITE_ID"
		);
	}

	return {
		url,
		siteId: parseInt(siteId, 10),
		heartBeat: {
			active: true,
			seconds: 15,
		},
		linkTracking: true,
		configurations: {
			// Enable secure cookie
			setSecureCookie: window.location.protocol === "https:",
			// Set cookie same site
			setCookieSameSite: "Lax",
			// Respect Do Not Track
			setDoNotTrack: true,
		},
	};
}

/**
 * Matomo Provider Component
 * Provides Matomo tracking functionality to the React component tree
 */
export function MatomoProvider({ children, config }: MatomoProviderProps) {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [hasConsent, setHasConsent] = useState(false);
	const [isOptedOut, setIsOptedOut] = useState(false);

	// Get configuration
	const matomoConfig = useMemo(() => {
		try {
			return config || getConfigFromEnv();
		} catch (err) {
			setError(err as Error);
			return null;
		}
	}, [config]);

	// Initialize Matomo service
	const matomoService = useMemo(() => {
		if (!matomoConfig) return null;

		const serviceConfig = {
			...matomoConfig,
			disabled: matomoConfig.disabled || isOptedOut,
		};

		return MatomoService.getInstance(serviceConfig);
	}, [matomoConfig, isOptedOut]);

	// Load Matomo script
	useEffect(() => {
		if (!matomoService) {
			setIsLoading(false);
			return;
		}

		// Check for stored consent/opt-out preferences
		const storedConsent = localStorage.getItem(
			LOCAL_STORAGE_KEYS.MATOMO_CONSENT
		);
		const storedOptOut = localStorage.getItem(
			LOCAL_STORAGE_KEYS.MATOMO_OPT_OUT
		);

		if (storedConsent !== null) {
			setHasConsent(storedConsent === "true");
		}

		if (storedOptOut !== null) {
			setIsOptedOut(storedOptOut === "true");
		}

		// Load script
		matomoService
			.loadScript()
			.then(() => {
				setIsLoading(false);

				// Apply consent settings
				const tracker = matomoService.getTracker();
				if (tracker) {
					// Always require consent initially
					tracker.requireConsent();

					// If user has previously given consent, apply it
					if (storedConsent === "true") {
						tracker.setConsentGiven();
					}
				}
			})
			.catch((err) => {
				setError(err as Error);
				setIsLoading(false);
			});
	}, [matomoService]);

	// Consent management
	const giveConsent = useCallback(() => {
		setHasConsent(true);
		localStorage.setItem(LOCAL_STORAGE_KEYS.MATOMO_CONSENT, "true");

		const tracker = matomoService?.getTracker();
		if (tracker) {
			tracker.setConsentGiven();
			tracker.rememberConsentGiven();
		}
	}, [matomoService]);

	const removeConsent = useCallback(() => {
		setHasConsent(false);
		localStorage.setItem(LOCAL_STORAGE_KEYS.MATOMO_CONSENT, "false");

		const tracker = matomoService?.getTracker();
		if (tracker) {
			tracker.forgetConsentGiven();
			tracker.requireConsent();
		}
	}, [matomoService]);

	// Opt-out management
	const optOut = useCallback(() => {
		setIsOptedOut(true);
		localStorage.setItem(LOCAL_STORAGE_KEYS.MATOMO_OPT_OUT, "true");

		// Update service configuration
		if (matomoService) {
			matomoService.updateConfig({ disabled: true });
		}

		// Delete cookies
		const tracker = matomoService?.getTracker();
		if (tracker) {
			tracker.deleteCookies();
		}
	}, [matomoService]);

	const optIn = useCallback(() => {
		setIsOptedOut(false);
		localStorage.setItem(LOCAL_STORAGE_KEYS.MATOMO_OPT_OUT, "false");

		// Update service configuration
		if (matomoService) {
			matomoService.updateConfig({ disabled: false });
		}
	}, [matomoService]);

	// Create context value
	const contextValue: MatomoContextValue = useMemo(
		() => ({
			isLoading,
			isEnabled: !!matomoService && !isOptedOut,
			hasConsent,
			error,
			tracker: matomoService?.getTracker() || null,
			giveConsent,
			removeConsent,
			optOut,
			optIn,
		}),
		[
			isLoading,
			matomoService,
			isOptedOut,
			hasConsent,
			error,
			giveConsent,
			removeConsent,
			optOut,
			optIn,
		]
	);

	// A configuration error must not take the context away with it. Returning bare
	// children here was the bug: analytics degraded by removing the provider, so the
	// first descendant to call `useMatomo()` threw "must be used within a
	// MatomoProvider" and the whole app fell into the root error boundary — a blank
	// "That's on us." page purely because VITE_MATOMO_SITE_ID was unset, which is the
	// normal state of every local dev run.
	//
	// The context value below already describes the failed case honestly:
	// `isEnabled: false`, `tracker: null`, and `error` set. Every consumer in
	// `hooks.ts` guards on `if (!tracker) return`, so analytics simply does nothing.
	if (error && !matomoConfig) {
		console.warn(
			"Matomo is disabled: no valid configuration. Analytics events will be dropped.",
			error.message
		);
	}

	return (
		<MatomoContext.Provider value={contextValue}>
			{children}
		</MatomoContext.Provider>
	);
}
